/** Page-turn sounds made with the Web Audio API and the browser's speech voice (no sound files needed). */
export type SoundId = 'paper' | 'click' | 'voice' | 'chime' | 'swish' | 'bell' | 'off';

export const SOUNDS: { id: SoundId; label: string }[] = [
  { id: 'paper', label: 'Paper click (next and previous differ)' },
  { id: 'click', label: 'Single click' },
  { id: 'voice', label: 'Voice: "Page 2"' },
  { id: 'chime', label: 'Soft chime' },
  { id: 'swish', label: 'Page swish' },
  { id: 'bell', label: 'Bell' },
  { id: 'off', label: 'No sound' },
];
export const SOUND_IDS = SOUNDS.map((s) => s.id);
export const isSoundId = (v: unknown): v is SoundId => typeof v === 'string' && (SOUND_IDS as string[]).includes(v);

const KEY = 'epaper.sound';
let ctx: AudioContext | null = null;
// On a shared page the reader cannot choose: the sound chosen by the person who shared it is used.
let override: SoundId | null = null;

export function setSoundOverride(s: SoundId | null): void {
  override = s;
}

export function getSoundMode(): SoundId {
  if (override) return override;
  try {
    const v = localStorage.getItem(KEY);
    if (v === 'different') return 'paper'; // names used by older versions
    if (v === 'same') return 'click';
    if (isSoundId(v)) return v;
  } catch {
    /* ignore */
  }
  return 'paper';
}

export function setSoundMode(m: SoundId): void {
  try {
    localStorage.setItem(KEY, m);
  } catch {
    /* ignore */
  }
}

function audio(): AudioContext | null {
  try {
    if (!ctx) {
      const AC: typeof AudioContext | undefined =
        window.AudioContext || (window as unknown as { webkitAudioContext?: typeof AudioContext }).webkitAudioContext;
      if (!AC) return null;
      ctx = new AC();
    }
    if (ctx.state === 'suspended') void ctx.resume();
    return ctx;
  } catch {
    return null;
  }
}

function click(freq: number, endFreq: number, vol: number): void {
  const a = audio();
  if (!a) return;
  const t = a.currentTime;
  // short tone
  const osc = a.createOscillator();
  const g = a.createGain();
  osc.type = 'triangle';
  osc.frequency.setValueAtTime(freq, t);
  osc.frequency.exponentialRampToValueAtTime(endFreq, t + 0.07);
  g.gain.setValueAtTime(0.0001, t);
  g.gain.exponentialRampToValueAtTime(vol, t + 0.005);
  g.gain.exponentialRampToValueAtTime(0.0001, t + 0.09);
  osc.connect(g).connect(a.destination);
  osc.start(t);
  osc.stop(t + 0.1);
  // tiny noise tick for a "paper" feel
  const len = Math.floor(a.sampleRate * 0.03);
  const buf = a.createBuffer(1, len, a.sampleRate);
  const d = buf.getChannelData(0);
  for (let i = 0; i < len; i++) d[i] = (Math.random() * 2 - 1) * (1 - i / len);
  const src = a.createBufferSource();
  const ng = a.createGain();
  ng.gain.value = vol * 0.5;
  src.buffer = buf;
  src.connect(ng).connect(a.destination);
  src.start(t);
}

/** A soft note with a quick attack and a smooth fade. */
function note(freq: number, start: number, dur: number, vol: number, type: OscillatorType = 'sine'): void {
  const a = audio();
  if (!a) return;
  const t = a.currentTime + start;
  const osc = a.createOscillator();
  const g = a.createGain();
  osc.type = type;
  osc.frequency.setValueAtTime(freq, t);
  g.gain.setValueAtTime(0.0001, t);
  g.gain.exponentialRampToValueAtTime(vol, t + 0.012);
  g.gain.exponentialRampToValueAtTime(0.0001, t + dur);
  osc.connect(g).connect(a.destination);
  osc.start(t);
  osc.stop(t + dur + 0.02);
}

/** Two notes going up (next) or down (previous). */
function chime(direction: 'next' | 'prev'): void {
  const [a, b] = direction === 'next' ? [660, 880] : [880, 660];
  note(a, 0, 0.22, 0.16);
  note(b, 0.11, 0.35, 0.16);
}

/** A band of noise sweeping up (next) or down (previous), like a page being flicked. */
function swish(direction: 'next' | 'prev'): void {
  const a = audio();
  if (!a) return;
  const t = a.currentTime;
  const len = Math.floor(a.sampleRate * 0.28);
  const buf = a.createBuffer(1, len, a.sampleRate);
  const d = buf.getChannelData(0);
  for (let i = 0; i < len; i++) d[i] = Math.random() * 2 - 1;
  const src = a.createBufferSource();
  src.buffer = buf;
  const f = a.createBiquadFilter();
  f.type = 'bandpass';
  f.Q.value = 1.2;
  const [from, to] = direction === 'next' ? [500, 3200] : [3200, 500];
  f.frequency.setValueAtTime(from, t);
  f.frequency.exponentialRampToValueAtTime(to, t + 0.26);
  const g = a.createGain();
  g.gain.setValueAtTime(0.0001, t);
  g.gain.exponentialRampToValueAtTime(0.28, t + 0.05);
  g.gain.exponentialRampToValueAtTime(0.0001, t + 0.27);
  src.connect(f).connect(g).connect(a.destination);
  src.start(t);
}

/** A bell: a pure tone plus two inharmonic partials, ringing out. */
function bell(direction: 'next' | 'prev'): void {
  const base = direction === 'next' ? 880 : 660;
  note(base, 0, 0.9, 0.17);
  note(base * 2.76, 0, 0.5, 0.06);
  note(base * 5.4, 0, 0.25, 0.03);
}

/** Says "Page N" with the browser's voice. Returns false when the browser has no speech voice. */
function say(page: number): boolean {
  try {
    const s = window.speechSynthesis;
    if (!s || typeof SpeechSynthesisUtterance === 'undefined') return false;
    s.cancel();
    const u = new SpeechSynthesisUtterance('Page ' + page);
    u.lang = 'en-IN';
    u.rate = 1.05;
    u.volume = 1;
    s.speak(u);
    return true;
  } catch {
    return false;
  }
}

/** Plays the chosen sound for a page turn. `page` is the page that is being opened (used by the voice). */
export function playTurn(direction: 'next' | 'prev', page?: number): void {
  const mode = getSoundMode();
  if (mode === 'off') return;
  if (mode === 'voice') {
    if (page !== undefined && say(page)) return;
    click(1000, 520, 0.18); // no speech voice in this browser: fall back to the paper click
    return;
  }
  if (mode === 'click' || (mode === 'paper' && direction === 'next')) click(1000, 520, 0.18);
  else if (mode === 'paper') click(620, 300, 0.2);
  else if (mode === 'chime') chime(direction);
  else if (mode === 'swish') swish(direction);
  else if (mode === 'bell') bell(direction);
}
